// ─── Bouton d'aide par fonction (Core) ───────────────────────────
// <AideFonction cle="calendrier" /> pose une icône ℹ️ (en-tête d'écran/carte).
// Au tap, ouvre une Feuille : intro + sections d'aide, puis, si présents, les
// tutoriels (étapes numérotées). Le contenu vient du registry AideProvider ; on
// peut aussi passer `contenu` en direct (sans registry).
import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, Platform } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import Feuille from '../ui/Feuille';
import { useAide } from './AideContext';
import { usePratique } from '../apprentissage/PratiqueContext';

const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

export default function AideFonction({ cle, contenu, style }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { aideDe } = useAide();
  const pratique = usePratique();
  const [ouvert, setOuvert] = useState(false);
  const aide = contenu || aideDe(cle);
  const meps = pratique?.pratiquesDe ? pratique.pratiquesDe(cle) : [];
  if (!aide) return null;
  // Le registre peut porter des CLÉS i18n : t(clé) traduit ; un texte libre qui
  // n'est pas une clé du catalogue revient tel quel (traduire → clé brute).
  const tr = (v) => (typeof v === 'string' ? t(v) : v);

  // Le bouton ℹ️ vit souvent dans un EN-TÊTE. Sur natif, la Feuille est un
  // calque ABSOLU relatif à son parent → ancrée dans l'en-tête, elle déborde de
  // l'écran. On l'enveloppe donc dans un <Modal> transparent plein écran (un
  // seul Modal natif — la Feuille n'en rend pas sur natif) ; sur web, la
  // Feuille rend déjà son propre Modal.
  const feuille = (
    <Feuille visible={ouvert} titre={tr(aide.titre) || t('aide.titre')} emoji={aide.emoji || "ℹ️"} hauteur={0.8} onFermer={() => setOuvert(false)}>
      {!!aide.intro && <Text style={s.intro}>{tr(aide.intro)}</Text>}
      {(aide.sections || []).map((sec, i) => (
        <View key={i} style={s.section}>
          {!!sec.titre && <Text style={s.secTitre}>{tr(sec.titre)}</Text>}
          <Text style={s.secTxt}>{tr(sec.texte)}</Text>
        </View>
      ))}
      {(aide.tutos || []).map((tuto, i) => (
        <View key={`t${i}`} style={s.tuto}>
          <Text style={s.tutoTitre}>🎓 {tr(tuto.titre)}</Text>
          {(tuto.etapes || []).map((et, k) => (
            <View key={k} style={s.etape}>
              <Text style={s.etapeNum}>{k + 1}</Text>
              <Text style={s.etapeTxt}>{tr(et)}</Text>
            </View>
          ))}
        </View>
      ))}
      {/* Mises en pratique de la fonction : lance le runner (BarrePratique) —
          la feuille se ferme pour laisser l'utilisateur manipuler l'app. */}
      {meps.map(m => (
        <TouchableOpacity key={m.cle} style={s.mep}
          onPress={() => { setOuvert(false); pratique.lancer(m); }}>
          <Text style={s.mepIc}>🏋️</Text>
          <View style={{ flex: 1 }}>
            <Text style={s.mepTitre} numberOfLines={1}>{tr(m.titre)}</Text>
            {!!m.sousTitre && <Text style={s.mepSous} numberOfLines={1}>{tr(m.sousTitre)}</Text>}
          </View>
          <Text style={s.mepBtn}>{t('apprentissage.sExercer')}</Text>
        </TouchableOpacity>
      ))}
    </Feuille>
  );

  return (
    <>
      <TouchableOpacity onPress={() => setOuvert(true)} hitSlop={HIT} style={style} accessibilityLabel={t('aide.titre')}>
        <FontAwesome5 name="info-circle" solid size={18} color={UI.accent} />
      </TouchableOpacity>
      {Platform.OS === 'web'
        ? feuille
        : (
          <Modal visible={ouvert} transparent animationType="none" onRequestClose={() => setOuvert(false)}>
            {feuille}
          </Modal>
        )}
    </>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ic: { fontSize: 17 },
  intro: { fontSize: 13, color: UI.ink, lineHeight: 19, marginBottom: 8 },
  section: { marginTop: 12 },
  secTitre: { fontSize: 13.5, fontWeight: '800', color: UI.ink, marginBottom: 3 },
  secTxt: { fontSize: 12.5, color: UI.muted, lineHeight: 18 },
  tuto: { marginTop: 16, backgroundColor: UI.card, borderRadius: 12, borderWidth: 1, borderColor: UI.border, padding: 12 },
  tutoTitre: { fontSize: 13.5, fontWeight: '800', color: UI.accent, marginBottom: 8 },
  etape: { flexDirection: 'row', gap: 9, marginBottom: 7, alignItems: 'flex-start' },
  etapeNum: { width: 20, height: 20, borderRadius: 10, backgroundColor: UI.accent, color: '#fff', fontSize: 11, fontWeight: '800', textAlign: 'center', lineHeight: 20, overflow: 'hidden' },
  etapeTxt: { flex: 1, fontSize: 12.5, color: UI.ink, lineHeight: 18 },
  mep: { flexDirection: 'row', alignItems: 'center', gap: 9, marginTop: 10, backgroundColor: UI.card, borderRadius: 12, borderWidth: 1, borderColor: UI.accent, borderStyle: 'dashed', padding: 11 },
  mepIc: { fontSize: 17 },
  mepTitre: { fontSize: 13, fontWeight: '800', color: UI.ink },
  mepSous: { fontSize: 11, color: UI.faint, marginTop: 1 },
  mepBtn: { fontSize: 12, fontWeight: '800', color: UI.accent },
});
